import Button from '@components/v2/ui/Button/Button';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, ButtonProps } from '@mui/material';
import { memo } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import { OrderSummaryTestIds } from '../../../../../../utils/testIds/OrderSummary.testIds';
import { useNetworkContext } from '../../../../../offline/contexts/network/network.context';
import { isOfflineStatus } from '../../../../../offline/domain/offlineOrder/offlineOrder.types';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useOrderContext } from '../../../../contexts/Order.context';
import useFinishFlow from '../../../../finishFlow/useFinishFlow';
import { StatusType } from '../../../../order.types';
import { useOrderEditSectionsContext } from '../../../sections/OrderEditSections.context';
import OrderValidationErrors from '../../../validate/OrderValidationErrors';
import { getOrderSummaryActionButtonStyles } from './orderSummaryActions.styles';

const FinishButton = ({ onClick }: ButtonProps) => {
  const { translate } = useTranslation();
  const { userHasInternet } = useNetworkContext();
  const { buttonOverride } = useFinishFlow();
  const { order } = useOrderContext();
  const { isValidatingOrderConditions, hasOrderErrors } = useOrderEditSectionsContext();
  const isBlockOrderBook = useOrderBookStore((state) => state.isBlockOrderBook);
  const isDisabled = buttonOverride?.isDisabled ?? (hasOrderErrors || isBlockOrderBook);

  const getButtonText = () => {
    if (buttonOverride?.label) {
      return buttonOverride?.label;
    }

    if (isOfflineStatus(order?.status) && userHasInternet) {
      return LangEnum.GLOBAL_SYNC;
    }

    if (isOfflineStatus(order?.status) && !userHasInternet) {
      return LangEnum.GLOBAL_DOWNLOAD_BACKUP;
    }

    return order?.status === StatusType.ON_APPROVAL
      ? LangEnum.GLOBAL_APPROVE
      : LangEnum.ORDER_SUMMARY_FINISH_ORDER_ACTION_LABEL;
  };

  return (
    <Tooltip TooltipContent={isDisabled ? <OrderValidationErrors isFinishOrderValidation /> : ''}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          disabled={isValidatingOrderConditions || isDisabled}
          onClick={buttonOverride?.onClick ?? onClick}
          testId={OrderSummaryTestIds.CONTINUE_BUTTON}
          sx={(theme) => ({
            ...getOrderSummaryActionButtonStyles(theme),
            '&:disabled': { backgroundColor: theme.palette.action.disabledBackground },
          })}
        >
          <Typography noWrap>{translate(getButtonText())}</Typography>
        </Button>
      </Box>
    </Tooltip>
  );
};

export default memo(FinishButton);
